import { useState, useEffect, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  apiGetTmShows,
  apiImportTmShows,
  apiImportTmTimezones,
  apiGetTmNewShows,
  apiGetTmHotels,
  apiImportTmHotels,
  type TmShowPreview,
  type TmNewShow,
  type TmHotelPreview,
} from '../api';
import PageHeader from '../components/PageHeader';
import { EVENT_TYPE_OPTIONS as EVENT_TYPE_LIST } from '../lib/showTypes';

// Review screen: list TM events (both bands), edit type/status/date, then import into tour_dates.
// Source unique des types (2026-10-17) : lib/showTypes.ts (promo + studio ajoutés).
const EVENT_TYPE_OPTIONS = EVENT_TYPE_LIST;
// Must match the tour_dates.status CHECK exactly (scheduled/confirmed/completed/
// cancelled). 'pending'/'tentative' are NOT valid DB values and would be
// silently normalized to 'scheduled' server-side — don't offer them.
const STATUS_OPTIONS = ['scheduled', 'confirmed', 'completed', 'cancelled'];

export default function ShowsImportReviewPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<TmShowPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped?: number } | null>(null);
  const [tzImporting, setTzImporting] = useState(false);
  const [tzResult, setTzResult] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGetTmShows()
      .then((r) => {
        if (cancelled) return;
        setItems(r.shows);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) { setError(e.message || 'Failed to load TM shows'); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, []);

  const setField = (idx: number, field: keyof TmShowPreview, value: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    try {
      const payload = items.map((it) => ({
        tmId: it.tmId,
        name: it.name,
        date: it.date,
        time: it.time || '',
        event_type: it.event_type || 'show',
        status: it.status || 'scheduled',
        band_id: it.band_id ?? null,
        notes: it.notes || '',
        tmType: it.tmType,
      }));
      const res = await apiImportTmShows(payload);
      setResult({ created: res.created, skipped: res.skipped ?? 0 });
    } catch (e: any) {
      setError(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportTimezones = async () => {
    setTzImporting(true);
    setTzResult(null);
    setError(null);
    try {
      const res = await apiImportTmTimezones();
      setTzResult(t('tmImport.timezonesResult', {
        updated: res.summary.updated ?? 0,
        skipped: res.summary.skipped ?? 0,
        noMatch: res.summary.noMatch ?? 0,
      }));
    } catch (e: any) {
      setError(e.message || 'Timezone import failed');
    } finally {
      setTzImporting(false);
    }
  };

  // --- New shows detection (read-only diff TM vs Bandz) ---
  const [newShowsLoading, setNewShowsLoading] = useState(false);
  const [newShows, setNewShows] = useState<TmNewShow[] | null>(null);

  const handleDetectNewShows = async () => {
    setNewShowsLoading(true);
    setError(null);
    try {
      const res = await apiGetTmNewShows();
      setNewShows(res.candidates || []);
    } catch (e: any) {
      setError(e.message || 'New shows detection failed');
    } finally {
      setNewShowsLoading(false);
    }
  };

  // --- New shows import (insert detected candidates into tour_dates) ---
  // Imports ALL detected candidates (past + future) idempotently: the backend
  // skips any (band + date + name) already present, so re-running never dupes.
  const [newShowsImporting, setNewShowsImporting] = useState(false);
  const [newShowsResult, setNewShowsResult] = useState<{ created: number; skipped?: number } | null>(null);

  const handleImportNewShows = async () => {
    if (!newShows || newShows.length === 0) return;
    setNewShowsImporting(true);
    setNewShowsResult(null);
    setError(null);
    try {
      const payload = newShows.map((c) => ({
        tmId: c.tmId,
        name: c.name,
        date: c.date,
        time: '',
        event_type: c.suggested_event_type || 'show',
        status: c.status || 'scheduled',
        band_id: c.band_id ?? null,
        notes: '',
        tmType: c.tmType || '',
        timeZone: c.timeZone || '',
      }));
      const res = await apiImportTmShows(payload);
      setNewShowsResult({ created: res.created, skipped: res.skipped ?? 0 });
      await handleDetectNewShows(); // refresh candidates after import
      apiGetTmShows().then((r) => setItems(r.shows)).catch(() => {}); // refresh main list
    } catch (e: any) {
      setError(e.message || 'New shows import failed');
    } finally {
      setNewShowsImporting(false);
    }
  };

  // --- Hotels import (event_info_blocks Hotel/Accommodation -> accommodations) ---
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [hotelsImporting, setHotelsImporting] = useState(false);
  const [hotels, setHotels] = useState<TmHotelPreview[] | null>(null);
  const [hotelsResult, setHotelsResult] = useState<string | null>(null);

  const handlePreviewHotels = async () => {
    setHotelsLoading(true);
    setHotelsResult(null);
    setError(null);
    try {
      const res = await apiGetTmHotels();
      setHotels(res.previews || []);
    } catch (e: any) {
      setError(e.message || 'Hotel preview failed');
    } finally {
      setHotelsLoading(false);
    }
  };

  const handleImportHotels = async () => {
    setHotelsImporting(true);
    setHotelsResult(null);
    setError(null);
    try {
      const res = await apiImportTmHotels();
      const s = res.summary || {};
      setHotelsResult(t('tmImport.hotelsResult', {
        created: s.created ?? 0,
        filled: s.filled ?? 0,
        exists: s.exists ?? 0,
        noShow: s.noShow ?? 0,
      }));
      await handlePreviewHotels(); // refresh preview after import
    } catch (e: any) {
      setError(e.message || 'Hotel import failed');
    } finally {
      setHotelsImporting(false);
    }
  };

  if (loading) return <div className="page" style={{ padding: 48, textAlign: 'center' }}><p style={{ color: 'var(--text-muted)' }}>{t('tmImport.loadingShows')}</p></div>;

  const counts: Record<string, number> = {};
  for (const it of items) counts[it.event_type] = (counts[it.event_type] || 0) + 1;

  return (
    <div ref={denseRef} className="page musician-edit-page" data-density="socle">
      <PageHeader
        title={t('tmImport.showsTitle')}
        actions={<button className="btn btn-secondary" onClick={() => navigate('/tour/shows')}>{t('tmImport.back')}</button>}
      />

      {error && <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}
      {result && <div style={{ color: 'var(--success)', marginBottom: 12 }}>✅ {t('tmImport.importedShows', { count: result.created })}{result.skipped ? ` (${t('tmImport.skippedShows', { count: result.skipped })})` : ''} <button className="btn btn-link" onClick={() => navigate('/tour/shows')}>{t('tmImport.viewShows')}</button></div>}
      {tzResult && <div style={{ color: 'var(--success)', marginBottom: 12 }}>✅ {tzResult}</div>}

      {items.length === 0 && !result && <p style={{ color: 'var(--text-muted)' }}>{t('tmImport.noShows')}</p>}

      {items.length > 0 && !result && (
        <p style={{ color: 'var(--text-dim)', marginBottom: 12 }}>
          {t('tmImport.eventsCount', { count: items.length })} ·{' '}
          {Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(' · ')}
        </p>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl-dense" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>
              <th style={{ padding: 8 }}>{t('tmImport.colDate')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colName')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colTmType')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colBandzType')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colStatus')}</th>
              <th style={{ padding: 8 }}>{t('tmImport.colNotes')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
                <td style={{ padding: 8 }}>
                  <input type="date" value={it.date} onChange={(e) => setField(i, 'date', e.target.value)} style={{ width: 140 }} />
                </td>
                <td style={{ padding: 8 }}>
                  <input type="text" value={it.name} onChange={(e) => setField(i, 'name', e.target.value)} style={{ width: 240 }} />
                </td>
                <td style={{ padding: 8, color: 'var(--text-muted)' }}>{it.tmType}</td>
                <td style={{ padding: 8 }}>
                  <select value={it.event_type} onChange={(e) => setField(i, 'event_type', e.target.value)}>
                    {EVENT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td style={{ padding: 8 }}>
                  <select value={it.status} onChange={(e) => setField(i, 'status', e.target.value)}>
                    {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td style={{ padding: 8 }}>{it.notes ? it.notes.slice(0, 40) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {items.length > 0 && !result && (
        <div className="form-actions" style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
            {importing ? t('tmImport.importing') : t('tmImport.importShows', { count: items.length })}
          </button>
          <button className="btn btn-secondary" onClick={handleImportTimezones} disabled={tzImporting}>
            {tzImporting ? t('tmImport.importing') : t('tmImport.importTimezone')}
          </button>
        </div>
      )}

      <hr style={{ margin: '28px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

      {/* ---- New shows detection (read-only diff TM vs Bandz) ---- */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 'var(--font-lg)', marginBottom: 8 }}>{t('tmImport.newShowsTitle')}</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{t('tmImport.newShowsHint')}</p>
        <button className="btn btn-secondary" onClick={handleDetectNewShows} disabled={newShowsLoading}>
          {newShowsLoading ? t('tmImport.loading') : t('tmImport.detectNewShows')}
        </button>
        {newShows && (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: 'var(--text-dim)', marginBottom: 8 }}>
              {newShows.length === 0
                ? t('tmImport.noNewShows')
                : t('tmImport.newShowsCount', { count: newShows.length })}
            </p>
            {newShows.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table className="tbl-dense" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-sm)' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>
                      <th style={{ padding: 8 }}>{t('tmImport.colDate')}</th>
                      <th style={{ padding: 8 }}>{t('tmImport.colName')}</th>
                      <th style={{ padding: 8 }}>{t('tmImport.colBand')}</th>
                      <th style={{ padding: 8 }}>{t('tmImport.colStatus')}</th>
                      <th style={{ padding: 8 }}>{t('tmImport.colTimezone')}</th>
                      <th style={{ padding: 8 }}>{t('tmImport.colTmType')}</th>
                      <th style={{ padding: 8 }}>{t('tmImport.colSuggestedType')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newShows.map((c, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
                        <td style={{ padding: 8 }}>{c.date || '—'}</td>
                        <td style={{ padding: 8 }}>{c.name || '—'}</td>
                        <td style={{ padding: 8 }}>{c.band || '—'}</td>
                        <td style={{ padding: 8 }}>{c.status || '—'}</td>
                        <td style={{ padding: 8 }}>{c.timeZone || '—'}</td>
                        <td style={{ padding: 8, color: 'var(--text-muted)' }}>{c.tmType || '—'}</td>
                        <td style={{ padding: 8 }}>{c.suggested_event_type || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {newShows.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={handleImportNewShows} disabled={newShowsImporting}>
                  {newShowsImporting ? t('tmImport.importing') : t('tmImport.importNewShows', { count: newShows.length })}
                </button>
                {newShowsResult && (
                  <div style={{ color: 'var(--success)', marginTop: 10 }}>
                    ✅ {t('tmImport.importedShows', { count: newShowsResult.created })}
                    {newShowsResult.skipped ? ` (${t('tmImport.skippedShows', { count: newShowsResult.skipped })})` : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <hr style={{ margin: '28px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

      {/* ---- Hotels import (Hotel/Accommodation -> accommodations) ---- */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 'var(--font-lg)', marginBottom: 8 }}>{t('tmImport.hotelsTitle')}</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{t('tmImport.hotelsHint')}</p>
        <div className="form-actions" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handlePreviewHotels} disabled={hotelsLoading}>
            {hotelsLoading ? t('tmImport.loading') : t('tmImport.previewHotels')}
          </button>
          <button className="btn btn-primary" onClick={handleImportHotels} disabled={hotelsImporting}>
            {hotelsImporting ? t('tmImport.importing') : t('tmImport.importHotels')}
          </button>
        </div>
        {hotelsResult && <div style={{ color: 'var(--success)', marginTop: 10 }}>✅ {hotelsResult}</div>}
        {hotels && (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: 'var(--text-dim)', marginBottom: 8 }}>{t('tmImport.hotelsCount', { count: hotels.length })}</p>
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl-dense" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-sm)' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>
                    <th style={{ padding: 8 }}>{t('tmImport.colDate')}</th>
                    <th style={{ padding: 8 }}>{t('tmImport.colBand')}</th>
                    <th style={{ padding: 8 }}>{t('tmImport.colShow')}</th>
                    <th style={{ padding: 8 }}>{t('tmImport.colHotel')}</th>
                    <th style={{ padding: 8 }}>{t('tmImport.colAddress')}</th>
                    <th style={{ padding: 8 }}>{t('tmImport.colConfirmation')}</th>
                    <th style={{ padding: 8 }}>{t('tmImport.colAction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {hotels.map((h, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
                      <td style={{ padding: 8 }}>{h.date || '—'}</td>
                      <td style={{ padding: 8 }}>{h.band || '—'}</td>
                      <td style={{ padding: 8 }}>{h.showName || h.name || '—'}</td>
                      <td style={{ padding: 8 }}>{h.hotelName || '—'}</td>
                      <td style={{ padding: 8 }}>{h.address || '—'}</td>
                      <td style={{ padding: 8 }}>{h.confirmation || '—'}</td>
                      <td style={{ padding: 8 }}>
                        {h.action === 'no-show' ? <span style={{ color: 'var(--danger)' }}>{t('tmImport.actionNoShow')}</span>
                          : h.action === 'exists' ? <span style={{ color: 'var(--text-dim)' }}>{t('tmImport.actionExists')}</span>
                          : h.action === 'fill' ? <span style={{ color: 'var(--warning)' }}>{t('tmImport.actionFill')}</span>
                          : <span style={{ color: 'var(--success)' }}>{t('tmImport.actionCreate')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}